import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { IconPlus, IconSearch, IconEdit, IconTrash, IconEye } from './Icons';
import { TENDER_STATUS_OPTIONS, APPROVED_OPTIONS } from '../data/tendersMock';
import { mapRowToTender } from '../lib/tenders';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin } from '../lib/roles';
import TenderForm from './TenderForm';
import './Tenders.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const tenderListSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

const DEPARTMENTS = ['Civil', 'IT', 'Mechanical', 'MEP', 'Electrical'];

function getStatusBadgeClass(status) {
  const s = (status || '').toLowerCase().replace(/\s+/g, '-');
  if (s === 'draft') return 'tenders-badge-draft';
  if (s === 'submitted') return 'tenders-badge-submitted';
  if (s === 'under-review') return 'tenders-badge-under-review';
  if (s === 'awarded') return 'tenders-badge-awarded';
  if (s === 'lost') return 'tenders-badge-lost';
  if (s === 'cancelled') return 'tenders-badge-cancelled';
  return 'tenders-badge-draft';
}

function getApprovedBadgeClass(approved) {
  const a = (approved || '').toLowerCase();
  if (a.includes('pending')) return 'tenders-badge-pending';
  if (a === 'approved') return 'tenders-badge-approved';
  if (a === 'rejected') return 'tenders-badge-rejected';
  return 'tenders-badge-not-approved';
}

export default function TenderList() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const [tenders, setTenders] = useState([]);
  const [tendersLoading, setTendersLoading] = useState(true);
  const [tendersError, setTendersError] = useState(null);
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [deadlineFrom, setDeadlineFrom] = useState('');
  const [deadlineTo, setDeadlineTo] = useState('');
  const [approvedFilter, setApprovedFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTender, setSelectedTender] = useState(null);
  const [showTenderForm, setShowTenderForm] = useState(false);
  const [toast, setToast] = useState(null);

  const [profiles, setProfiles] = useState([]);
  const [assignments, setAssignments] = useState({}); // tender_id -> user_id[]
  const [openAssignmentTenderId, setOpenAssignmentTenderId] = useState(null);
  const [assignmentSaving, setAssignmentSaving] = useState(false);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      tenderListSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchTenders = useCallback(async () => {
    setTendersLoading(true);
    setTendersError(null);
    try {
      await runWakeUp();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const res = await Promise.race([
        tenderListSupabase.from('tenders').select('*').order('created_at', { ascending: false }),
        timeoutPromise,
      ]);
      const { data, error } = res;
      if (error) {
        setTendersError(error.message);
        setTenders([]);
        setTendersLoading(false);
        return;
      }
      const tenderList = (data || []).map(mapRowToTender);
      setTenders(tenderList);

      const [profilesRes, assignmentsRes] = await Promise.all([
        tenderListSupabase.from('profiles').select('id, full_name').order('full_name'),
        tenderListSupabase.from('tender_assignments').select('tender_id, user_id'),
      ]);
      if (profilesRes.data) setProfiles(profilesRes.data);
      if (assignmentsRes.data) {
        const byTender = {};
        assignmentsRes.data.forEach(({ tender_id, user_id }) => {
          if (!byTender[tender_id]) byTender[tender_id] = [];
          byTender[tender_id].push(user_id);
        });
        setAssignments(byTender);
      }
    } catch (e) {
      setTendersError(e?.message || 'Failed to load');
      setTenders([]);
    } finally {
      setTendersLoading(false);
    }
  }, [runWakeUp]);

  const refreshSessionAndFetch = useCallback(async () => {
    setTendersError(null);
    try {
      await tenderListSupabase.auth.getSession();
      await runWakeUp();
      await fetchTenders();
    } catch (e) {
      setTendersError(e?.message || 'Failed to load');
    }
  }, [runWakeUp, fetchTenders]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { error: sessionErr } = await tenderListSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setTendersError(sessionErr.message);
        setTendersLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await fetchTenders();
    })();
    return () => { cancelled = true; };
  }, [runWakeUp, fetchTenders]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const clientsFromData = useMemo(() => [...new Set(tenders.map((t) => t.client).filter(Boolean))].sort(), [tenders]);

  const enrichedTenders = useMemo(() => {
    return tenders.map((t) => {
      const userIds = assignments[t.id] || [];
      const names = userIds.map((uid) => profiles.find((p) => p.id === uid)?.full_name).filter(Boolean);
      return { ...t, assignmentUserIds: userIds, assignmentNames: names };
    });
  }, [tenders, assignments, profiles]);

  const filteredTenders = useMemo(() => {
    return enrichedTenders.filter((t) => {
      const q = search.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (t.tenderId || '').toLowerCase().includes(q) ||
        (t.title || '').toLowerCase().includes(q) ||
        (t.client || '').toLowerCase().includes(q);
      const matchesClient = !clientFilter || t.client === clientFilter;
      const matchesStatus = !statusFilter || t.status === statusFilter;
      const matchesDept = !departmentFilter || t.department === departmentFilter;
      const matchesApproved = !approvedFilter || approvedFilter === 'All' || t.approved === approvedFilter;
      const matchesAssigned = !assignedFilter || t.assignmentNames?.includes(assignedFilter) || t.assignedTo === assignedFilter;
      const deadline = t.submissionDeadline || '';
      const matchesFrom = !deadlineFrom || (deadline && deadline >= deadlineFrom);
      const matchesTo = !deadlineTo || (deadline && deadline <= deadlineTo);
      return matchesSearch && matchesClient && matchesStatus && matchesDept && matchesApproved && matchesAssigned && matchesFrom && matchesTo;
    });
  }, [enrichedTenders, search, clientFilter, statusFilter, departmentFilter, deadlineFrom, deadlineTo, approvedFilter, assignedFilter]);

  const totalCount = filteredTenders.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const currentPage = Math.min(page, totalPages);
  const paginatedTenders = filteredTenders.slice((currentPage - 1) * perPage, currentPage * perPage);
  const from = totalCount === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const to = Math.min(currentPage * perPage, totalCount);

  const openDeleteModal = (tender) => {
    setSelectedTender(tender);
    setShowDeleteModal(true);
  };

  const toggleAssignment = useCallback(async (tenderId, userId, checked) => {
    setAssignmentSaving(true);
    await runWakeUp();
    const { error } = checked
      ? await tenderListSupabase.from('tender_assignments').insert({ tender_id: tenderId, user_id: userId })
      : await tenderListSupabase.from('tender_assignments').delete().eq('tender_id', tenderId).eq('user_id', userId);
    if (error) {
      setToast(error.message || 'Failed to update assignment');
      setTimeout(() => setToast(null), 3000);
    } else {
      setAssignments((prev) => {
        const list = prev[tenderId] ? [...prev[tenderId]] : [];
        if (checked) {
          if (!list.includes(userId)) list.push(userId);
          return { ...prev, [tenderId]: list };
        }
        const nextList = list.filter((id) => id !== userId);
        const next = { ...prev };
        if (nextList.length === 0) delete next[tenderId];
        else next[tenderId] = nextList;
        return next;
      });
    }
    setAssignmentSaving(false);
  }, [runWakeUp]);

  const renderAssignmentCell = (row) => {
    const isOpen = openAssignmentTenderId === row.id;
    const names = row.assignmentNames || [];
    const userIds = row.assignmentUserIds || [];
    return (
      <div className="tenders-assignment-cell">
        <button
          type="button"
          className="tenders-assignment-trigger"
          onClick={() => setOpenAssignmentTenderId(isOpen ? null : row.id)}
          disabled={assignmentSaving}
          title="Select assigned users"
        >
          {names.length === 0 ? '—' : names.length === 1 ? names[0] : `${names.length} users`}
        </button>
        {isOpen && (
          <>
            <div className="tenders-assignment-backdrop" onClick={() => setOpenAssignmentTenderId(null)} aria-hidden />
            <div className="tenders-assignment-dropdown">
              {profiles.length === 0 ? (
                <div className="tenders-assignment-empty">No users in team</div>
              ) : (
                profiles.map((p) => {
                  const checked = userIds.includes(p.id);
                  return (
                    <label key={p.id} className="tenders-assignment-option">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleAssignment(row.id, p.id, e.target.checked);
                        }}
                      />
                      <span>{p.full_name || p.id}</span>
                    </label>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const handleExportExcel = () => {
    const header = ['Tender ID', 'Title', 'Client', 'Department', 'Submission deadline', 'Status', 'Approved', 'Assigned to', 'Created'];
    const rows = filteredTenders.map((t) => [
      t.tenderId,
      t.title,
      t.client,
      t.department,
      t.submissionDeadline,
      t.status,
      t.approved,
      (t.assignmentNames && t.assignmentNames.length ? t.assignmentNames.join(', ') : t.assignedTo) || '',
      t.created,
    ]);
    const csvLines = [header, ...rows].map((cols) =>
      cols.map((v) => (String(v).includes(',') || String(v).includes('"') ? `"${String(v).replace(/"/g, '""')}"` : v)).join(',')
    );
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'tenders-export.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTender?.id) return;
    await runWakeUp();
    const { error } = await tenderListSupabase.from('tenders').delete().eq('id', selectedTender.id);
    if (error) {
      setToast('Failed to delete tender.');
      setTimeout(() => setToast(null), 3000);
      return;
    }
    setShowDeleteModal(false);
    setSelectedTender(null);
    setToast('Tender deleted.');
    setTimeout(() => setToast(null), 3000);
    fetchTenders();
  };

  const handleTenderFormSuccess = () => {
    setShowTenderForm(false);
    setSelectedTender(null);
    setToast('Tender saved successfully.');
    setTimeout(() => setToast(null), 3000);
    fetchTenders();
  };

  const openAddTender = () => {
    setSelectedTender(null);
    setShowTenderForm(true);
  };

  const openEditTender = (tender) => {
    setSelectedTender(tender);
    setShowTenderForm(true);
  };

  if (!isAdmin(role)) {
    return (
      <div className="tenders-page-card" style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ fontSize: '1.1rem', color: 'var(--text-muted)' }}>
          You need access to see this information.
        </p>
      </div>
    );
  }

  return (
    <div className="tenders-page-card">
      <div className="tenders-page-header">
        <h2 className="tenders-page-title">Tender List</h2>
        <div className="tenders-header-actions">
          <button type="button" className="tenders-header-btn primary" onClick={openAddTender}>
            <IconPlus />
            Add Tender
          </button>
        </div>
      </div>

      <div className="tenders-filter-row">
        <div className="tenders-filter-left">
          <select className="tenders-filter-select" value={clientFilter} onChange={(e) => { setClientFilter(e.target.value); setPage(1); }}>
            <option value="">Client</option>
            {clientsFromData.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select className="tenders-filter-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">Status</option>
            {TENDER_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select className="tenders-filter-select" value={departmentFilter} onChange={(e) => { setDepartmentFilter(e.target.value); setPage(1); }}>
            <option value="">Department</option>
            {DEPARTMENTS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <div className="tenders-date-filter">
            <label>Submission deadline from</label>
            <input type="date" value={deadlineFrom} onChange={(e) => { setDeadlineFrom(e.target.value); setPage(1); }} />
          </div>
          <div className="tenders-date-filter">
            <label>to</label>
            <input type="date" value={deadlineTo} onChange={(e) => { setDeadlineTo(e.target.value); setPage(1); }} />
          </div>
          <select className="tenders-filter-select" value={approvedFilter} onChange={(e) => { setApprovedFilter(e.target.value); setPage(1); }}>
            <option value="">Approved</option>
            <option value="All">All</option>
            {APPROVED_OPTIONS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <select className="tenders-filter-select" value={assignedFilter} onChange={(e) => { setAssignedFilter(e.target.value); setPage(1); }}>
            <option value="">Assigned to</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.full_name || ''}>{p.full_name || p.id}</option>
            ))}
          </select>
        </div>
        <div className="tenders-filter-right">
          <button type="button" className="tenders-utility-btn" onClick={handleExportExcel}>Excel</button>
          <div className="tenders-search">
            <input
              type="text"
              placeholder="Search"
              aria-label="Search tenders"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
            <span className="tenders-search-icon"><IconSearch /></span>
          </div>
        </div>
      </div>

      <div className="tenders-table-wrapper">
        <table className="tenders-table">
          <thead>
            <tr>
              <th>Tender ID</th>
              <th>Title / Tender name</th>
              <th>Client</th>
              <th>Department</th>
              <th>Submission deadline</th>
              <th>Status</th>
              <th>Approved</th>
              <th>Assigned to</th>
              <th>Created date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tendersLoading ? (
              <tr>
                <td colSpan={10} className="tenders-empty-cell">Loading…</td>
              </tr>
            ) : tendersError ? (
              <tr>
                <td colSpan={10} className="tenders-empty-cell" style={{ color: 'var(--danger)' }}>{tendersError}</td>
              </tr>
            ) : paginatedTenders.length === 0 ? (
              <tr>
                <td colSpan={10} className="tenders-empty-cell">No tenders found.</td>
              </tr>
            ) : (
              paginatedTenders.map((row) => (
                <tr key={row.id}>
                  <td>{row.tenderId}</td>
                  <td>
                    <button
                      type="button"
                      className="tenders-table-link-btn"
                      onClick={() => navigate(`/tenders/${row.id}`)}
                    >
                      {row.title}
                    </button>
                  </td>
                  <td>{row.client}</td>
                  <td>{row.department}</td>
                  <td>{row.submissionDeadline || '—'}</td>
                  <td>
                    <span className={`tenders-badge ${getStatusBadgeClass(row.status)}`}>{row.status}</span>
                  </td>
                  <td>
                    <span className={`tenders-badge ${getApprovedBadgeClass(row.approved)}`}>{row.approved}</span>
                  </td>
                  <td>{renderAssignmentCell(row)}</td>
                  <td>{row.created || '—'}</td>
                  <td>
                    <div className="tenders-row-actions">
                      <button type="button" className="tenders-row-icon-btn" aria-label="View" onClick={() => navigate(`/tenders/${row.id}`)} title="View">
                        <IconEye />
                      </button>
                      <button type="button" className="tenders-row-icon-btn" aria-label="Edit" onClick={() => openEditTender(row)} title="Edit">
                        <IconEdit />
                      </button>
                      <button type="button" className="tenders-row-icon-btn" aria-label="Delete" onClick={() => openDeleteModal(row)} title="Delete">
                        <IconTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="tenders-footer-row">
        <div className="tenders-footer-left">
          <select className="tenders-page-size" value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span>{from}-{to} / {totalCount}</span>
        </div>
        <div className="tenders-footer-right">
          <button type="button" className="tenders-page-btn" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>«</button>
          <button type="button" className="tenders-page-btn" disabled>{currentPage}</button>
          <button type="button" className="tenders-page-btn" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>»</button>
        </div>
      </div>

      {showTenderForm && (
        <TenderForm
          editTender={selectedTender}
          onClose={() => { setShowTenderForm(false); setSelectedTender(null); }}
          onSuccess={handleTenderFormSuccess}
        />
      )}

      {toast && <div className="tenders-toast" role="status">{toast}</div>}

      {/* Delete confirmation */}
      {showDeleteModal && selectedTender && (
        <div className="tenders-modal-backdrop" onClick={() => setShowDeleteModal(false)}>
          <div className="tenders-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tenders-modal-header">
              <h2 className="tenders-modal-title">Delete?</h2>
              <button type="button" className="tenders-modal-close" onClick={() => setShowDeleteModal(false)} aria-label="Close">×</button>
            </div>
            <div className="tenders-modal-body">
              <p>Are you sure you want to delete &quot;{selectedTender.title}&quot;? This action cannot be undone.</p>
            </div>
            <div className="tenders-modal-footer">
              <button type="button" className="tenders-footer-btn tenders-footer-btn-danger" onClick={handleDeleteConfirm}>Delete</button>
              <button type="button" className="tenders-footer-btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
